
import React, { useEffect, useRef, useMemo } from 'react';
import Icon, { SettingFilled } from '@ant-design/icons';
import classNames from 'classnames';
import SequenceWidgetProcessor, { SequenceWidgetState, getDefaultState } from '@kemu-io/kemu-core/widgets/sequence/index.js';
import { WidgetPortContext } from '@kemu-io/kemu-core/types';
import { GetPortsInformationFunction, GateUI, GateUIProps, GateCustomSettingsProps } from '../index';
import GateIcon from '../../gateIcon/gateIcon';
import { ReactComponent as SequenceWidgetIcon } from '../../../assets/img/gates/sequence-widget.svg';
import { PortLocation } from '../../../types/canvas_t';
import styles from './sequence.module.css';
import useReactiveWidgetState from '@hooks/useReactiveWidgetState';
import { DataType, WidgetPort } from '@kemu-io/hs-types';
import useTranslation from '@hooks/useTranslation';
import NumericInput from '../../WidgetsComponents/NumericInput/NumericInput';
import { SETTINGS_CONTAINER_CLASS } from '../../../common/constants';
import { removeWidgetPortConnections } from '@src/app/recipe/utils';
import { PortDescription } from '../index';

const DefaultWidgetHeight = 55;
const DefaultPortCount = 3;

/**
 * Calculates widget height based on the number of ports
 */
const calculateWidgetHeight = (outputs: number | undefined): number => {
	// 17 pixels per port
	const portCount = outputs ?? DefaultPortCount;
	const portsHeight = (portCount * 17);
	return portsHeight;
};

const compareState = (prevState: SequenceWidgetState, nextState: SequenceWidgetState): boolean => {
	const changed = prevState.$$lastEventType !== nextState.$$lastEventType ||
		prevState.outputPortCount !== nextState.outputPortCount;
	return changed;
};

const SequenceWidget = (props: GateUIProps): React.JSX.Element => {
	const { redrawPortsAndConnections, recipeId, thingRecipeId, info } = props;
	const [state] = useReactiveWidgetState<SequenceWidgetState>(recipeId, thingRecipeId, info.id, compareState);
	const fixedState = {
		...getDefaultState(),
		...state,
	};
	const previouslyDrawnEventType = useRef<DataType | undefined>(undefined);
	const previousOutputPortCount = useRef<number | undefined>(undefined);
	
	const portContext: WidgetPortContext = useMemo(() => ({
		recipePoolId: recipeId,
		recipeType: props.recipeType,
		thingRecipeId: thingRecipeId,
		widgetId: info.id,
	}), [recipeId, props.recipeType, thingRecipeId, info.id]);

	// Calculate widget height based on number of output ports
	const portsHeight = useMemo(() => {
		const stateForPorts: SequenceWidgetState = {
			...getDefaultState(),
			...state,
		};
		const outputNames = SequenceWidgetProcessor.getOutputNames(stateForPorts, portContext);
		return calculateWidgetHeight(outputNames.length);
	}, [state, portContext]);
	
	// Repaint widget when output type changes
	useEffect(() => {
		const changed = previouslyDrawnEventType.current !== state.$$lastEventType;
		if(state.$$lastEventType && changed){
			previouslyDrawnEventType.current = state.$$lastEventType;
			redrawPortsAndConnections();
		}
	}, [state.$$lastEventType, redrawPortsAndConnections]);

	// Repaint widget when output port count changes
	useEffect(() => {
		const currentPortCount = fixedState.outputPortCount ?? DefaultPortCount;
		if (previousOutputPortCount.current !== undefined && previousOutputPortCount.current !== currentPortCount) {
			redrawPortsAndConnections();
		}
		previousOutputPortCount.current = currentPortCount;
	}, [fixedState.outputPortCount, redrawPortsAndConnections]);

	return (
		<div 
			className={classNames(styles.GateBody, {
				[styles.WidgetDisabled]: props.info.disabled,
			})}
			style={{
				minHeight: `${DefaultWidgetHeight}px`,
				...(portsHeight > DefaultWidgetHeight ? {
					height: `${portsHeight}px`,
				} : {}),
			}}
		>
			<span className={styles.SettingsBtn} onClick={props.openCustomSettings}>
				<SettingFilled />
			</span>
			<Icon component={SequenceWidgetIcon}/>
		</div>
	);
};

/** Icon to be added to the bar */
const GateBarIcon = (): React.JSX.Element => {
	return (
		<GateIcon icon={<Icon component={SequenceWidgetIcon} />}/>
	);
};

const GateCustomSettings = (props: GateCustomSettingsProps): React.JSX.Element => {
	const { recipeId, blockId, gateInfo, redrawPortsAndConnections } = props;
	const [state, setState] = useReactiveWidgetState<SequenceWidgetState>(recipeId, blockId, gateInfo.id);
	const fixedState: SequenceWidgetState = {
		...getDefaultState(),
		...state,
	};

	const t = useTranslation('LogicMaker.Gates.Sequence.Settings');

	const handleInputChange = (value: number) => {
		// Validate range (2-10)
		const validatedValue = Math.max(2, Math.min(10, value));
		
		const currentPortCount = fixedState.outputPortCount ?? DefaultPortCount;
		
		if (validatedValue < currentPortCount) {
			// Remove connections from ports that will be removed (ports are numbered 1, 2, 3, etc.)
			for (let i = validatedValue; i < currentPortCount; i++) {
				const portName = String(i + 1);
				removeWidgetPortConnections(
					recipeId,
					blockId,
					gateInfo.id,
					portName
				);
			}
		}

		setState({
			...fixedState,
			outputPortCount: validatedValue,
		}, true);
	};

	return (
		<div className={classNames(styles.SettingsContainer, SETTINGS_CONTAINER_CLASS)}>
			{props.children}
			<div className={styles.InputContainer}>
				<label>{t('OutputPorts', 'Output ports')}</label>
				<NumericInput
					className={styles.NumericInput}
					min={2}
					max={10}
					value={fixedState.outputPortCount ?? DefaultPortCount}
					onChange={handleInputChange}
				/>
			</div>
		</div>
	);
};

/**
 * Builds output ports with evenly distributed positions
 * Uses a simple formula: (i + 1) / (totalOutputs + 1) for uniform spacing
 */
const buildOutputPorts = (ports: WidgetPort[]): PortDescription[] => {
	const totalOutputs = ports.length || 1;
	return ports.map((output, i) => ({
		name: output.name,
		type: output.type,
		jsonShape: output.jsonShape,
		position: [1, (i + 1) / (totalOutputs + 1), 1, 0] as PortLocation,
	}));
};

const getPortsInformation: GetPortsInformationFunction = (state, widgetInfo) => {
	const portContext: WidgetPortContext = {
		recipePoolId: widgetInfo.recipePoolId,
		recipeType: widgetInfo.recipeType,
		thingRecipeId: widgetInfo.thingRecipeId,
		widgetId: widgetInfo.id,
	};
	const outputNames = SequenceWidgetProcessor.getOutputNames(state, portContext);
	const inputNames = SequenceWidgetProcessor.getInputNames(state, portContext);

	const outputs = buildOutputPorts(outputNames);
	const positions: Record<string, PortLocation> = {
		'event': 'Left',
	};

	return {
		inputs: inputNames.map(input => ({
			name: input.name,
			type: input.type,
			position: positions[input.name]
		})),

		outputs,
	};
};

export default {
	getPortsInformation,
	BarIcon: GateBarIcon,
	Element: SequenceWidget,
	settingsCustomSize: true,
	CustomSettingsDialog: GateCustomSettings,
	hasTitle: false,
	getWidgetTitle: (intl) => intl.formatMessage({ defaultMessage: 'Sequence', id: 'LogicMapper.Widgets.Sequence.Title' }),
} as GateUI;
